/**
 * HTML5 History API Router for Single-Page Application
 * Enables URL-based navigation without page reloads
 * Supports bookmarks, browser back/forward, and right-click "Open in new tab"
 */

class Router {
    constructor() {
        this.routes = new Map();
        this.currentPath = window.location.pathname;
        
        // Listen for browser back/forward button clicks
        window.addEventListener('popstate', (e) => {
            const path = window.location.pathname;
            this.handleRoute(path, false);
        });
    }

    /**
     * Register a route with its handler function
     * @param {string} path - URL path (e.g., '/dashboard', '/terminal')
     * @param {Function} handler - Function to call when route is accessed
     */
    addRoute(path, handler) {
        this.routes.set(path, handler);
    }

    /**
     * Navigate to a new path programmatically
     * @param {string} path - URL path to navigate to
     * @param {boolean} pushState - Whether to add to browser history (default: true)
     */
    navigate(path, pushState = true) {
        // Normalize path
        if (path === '' || path === '/') {
            path = '/dashboard';
        }
        
        // Update browser URL if needed
        if (pushState && window.location.pathname !== path) {
            window.history.pushState({}, '', path);
        }
        
        this.handleRoute(path);
    }

    /**
     * Handle route by calling registered handler
     * @param {string} path - URL path to handle
     * @param {boolean} updateHistory - Whether to update history (default: true)
     */
    handleRoute(path, updateHistory = true) {
        // Normalize path
        if (path === '' || path === '/') {
            path = '/dashboard';
        }
        
        this.currentPath = path;
        
        // Find and execute route handler
        const handler = this.routes.get(path);
        if (handler && typeof handler === 'function') {
            handler();
        } else {
            // Route not found - redirect to dashboard
            // console.warn(`Route not found: ${path}, redirecting to dashboard`);
            this.navigate('/dashboard', updateHistory);
        }
    }

    /**
     * Get current view ID from path
     * @returns {string} - View identifier (e.g., 'dashboard', 'terminal')
     */
    getCurrentViewId() {
        // Remove leading slash: '/dashboard' -> 'dashboard'
        return this.currentPath.slice(1) || 'dashboard';
    }

    /**
     * Check if a path is currently active
     * @param {string} path - Path to check
     * @returns {boolean} - Whether path is current
     */
    isActive(path) {
        return this.currentPath === path;
    }
}

// Export for ES6 modules
export { Router };
